import { faUserCowboy } from '@fortawesome/pro-regular-svg-icons';
import { faPeopleGroup } from '@fortawesome/pro-regular-svg-icons';
import { faUserPen } from '@fortawesome/pro-regular-svg-icons';
import { faBuildings } from '@fortawesome/pro-regular-svg-icons';
import { faEarthAmericas } from '@fortawesome/pro-regular-svg-icons';
import { faLocationDot } from '@fortawesome/pro-regular-svg-icons';
import { faUserMagnifyingGlass } from '@fortawesome/pro-regular-svg-icons';
import { faIndustryWindows } from '@fortawesome/pro-regular-svg-icons';
import { faPersonChalkboard } from '@fortawesome/pro-regular-svg-icons';
import { faBuildingColumns } from '@fortawesome/pro-regular-svg-icons';
import { faIdCard } from '@fortawesome/pro-regular-svg-icons';
import { faSquareS } from '@fortawesome/pro-regular-svg-icons';
import { faDisplayCode } from '@fortawesome/pro-regular-svg-icons';
import { faBracketsCurly } from '@fortawesome/pro-regular-svg-icons';
import { faHeadset } from '@fortawesome/pro-regular-svg-icons';
import { Link } from 'react-router-dom';
import { useFeatureIsOn } from '@growthbook/growthbook-react';

// mui
import {
  List,
  ListItem as MuiListItem,
  ListItemText as MuiListItemText,
  ListSubheader,
  useTheme,
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClipboardList } from '@fortawesome/pro-solid-svg-icons';
import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import { ListItemIcon } from './AgencyMenuOptionsPopover';
import { Allow } from '../../../../../components/auth/Allow';
import { getIsUsPlatform } from '../../../../../utils';
import { usePlatformLanguage } from '../../../../../utils/hooks/usePlatformLanguage';
import { Show } from '../../../../../components/Show';
import { useCowbellTranslations } from '../../../../../i18n/translations';
import { FEATURE_FLAG_KEYS } from '../../../../../config/growthbook';

const ListItem = (props: any) => {
  const theme = useTheme<any>();
  return (
    <Allow allow={props.allow ?? 'any'}>
      <MuiListItem
        button
        component={Link}
        {...props}
        style={{ color: theme.agencyDash.text.link }}
      >
        {props.children}
      </MuiListItem>
    </Allow>
  );
};

const ListItemText = ({ primary }: { primary: string }) => (
  <MuiListItemText primary={primary} data-qa={`MENU_ITEM:${primary}`} />
);

interface MenuOptionsProps {
  onClick: (name: string, to: string) => void;
}

export const AdministrativeMenuOptions = ({ onClick }: MenuOptionsProps) => {
  const region = usePlatformLanguage();
  const isUSPlatform = getIsUsPlatform(region);
  const { t } = useCowbellTranslations();
  const crsWizerForAgencies = useFeatureIsOn(
    FEATURE_FLAG_KEYS.CRS_WIZER_FOR_AGENCIES
  );

  return (
    <List
      dense
      subheader={
        <ListSubheader style={{ fontWeight: 'bold', fontSize: '1.33rem' }}>
          Admin
        </ListSubheader>
      }
    >
      <ListItem
        allow={['roles:view']}
        to={AdministrativeMenuOptionsLinks.ROLES}
        onClick={onClick}
      >
        <ListItemIcon>
          <FontAwesomeIcon size="lg" icon={faUserCowboy} />
        </ListItemIcon>
        <ListItemText primary="Roles" />
      </ListItem>
      <ListItem
        allow={['teams:manage']}
        to={AdministrativeMenuOptionsLinks.TEAMS}
        onClick={onClick}
      >
        <ListItemIcon>
          <FontAwesomeIcon
            size="lg"
            icon={faPeopleGroup}
            style={{ position: 'relative', right: '0.2rem' }}
          />
        </ListItemIcon>
        <ListItemText primary="Teams" />
      </ListItem>
      <ListItem
        allow={['users:manage']}
        to={AdministrativeMenuOptionsLinks.USERS}
        onClick={onClick}
      >
        <ListItemIcon>
          <FontAwesomeIcon size="lg" icon={faUserPen} />
        </ListItemIcon>
        <ListItemText primary="Manage Users" />
      </ListItem>
      <ListItem
        allow={['agency_profile:manage']}
        to={AdministrativeMenuOptionsLinks.AGENCY_PROFILE}
        onClick={onClick}
      >
        <ListItemIcon>
          <FontAwesomeIcon size="lg" icon={faBuildings} />
        </ListItemIcon>
        <ListItemText primary={`${t('agency')} Profile`} />
      </ListItem>

      <Show when={isUSPlatform}>
        <ListItem
          allow={['roles:view']}
          to={AdministrativeMenuOptionsLinks.SUPPORTED_STATES}
          onClick={onClick}
        >
          <ListItemIcon>
            <FontAwesomeIcon size="lg" icon={faEarthAmericas} />
          </ListItemIcon>
          <ListItemText primary="Supported States" />
        </ListItem>
      </Show>

      <ListItem
        allow={['agency_locations:manage']}
        to={AdministrativeMenuOptionsLinks.LOCATIONS}
        onClick={onClick}
      >
        <ListItemIcon>
          <FontAwesomeIcon
            size="lg"
            icon={faLocationDot}
            style={{ position: 'relative', left: '0.2rem' }}
          />
        </ListItemIcon>
        <ListItemText primary="Locations" />
      </ListItem>
      <ListItem
        allow={['duns:lookup']}
        to={AdministrativeMenuOptionsLinks.ACCOUNT_LOOKUP}
        onClick={onClick}
      >
        <ListItemIcon>
          <FontAwesomeIcon size="lg" icon={faUserMagnifyingGlass} />
        </ListItemIcon>
        <ListItemText primary="Account Lookup" />
      </ListItem>
      <ListItem
        allow={['naics:view']}
        to={AdministrativeMenuOptionsLinks.INDUSTRY_LOOKUP}
        onClick={onClick}
      >
        <ListItemIcon>
          <FontAwesomeIcon size="lg" icon={faIndustryWindows} />
        </ListItemIcon>
        <ListItemText primary="Industry Lookup" />
      </ListItem>
      {crsWizerForAgencies && (
        <ListItem
          allow={['naics:view']}
          to={AdministrativeMenuOptionsLinks.TRAINING}
          onClick={onClick}
        >
          <ListItemIcon>
            <FontAwesomeIcon size="lg" icon={faPersonChalkboard} />
          </ListItemIcon>
          <ListItemText primary="Training" />
        </ListItem>
      )}
    </List>
  );
};

const AdministrativeMenuOptionsLinks = {
  ROLES: '/agency/manage/roles',
  TEAMS: '/agency/manage/teams',
  USERS: '/agency/manage/users',
  AGENCY_PROFILE: '/agency/manage/profile',
  SUPPORTED_STATES: '/agency/manage/states',
  LOCATIONS: '/agency/manage/locations',
  ACCOUNT_LOOKUP: '/agency/manage/account/lookup',
  INDUSTRY_LOOKUP: '/agency/manage/industry',
  TRAINING: '/agency/training',
};

export const LicensesMenuOptions = ({ onClick }: MenuOptionsProps) => {
  return (
    <List
      dense
      subheader={
        <ListSubheader style={{ fontWeight: 'bold', fontSize: '1.33rem' }}>
          Licenses
        </ListSubheader>
      }
    >
      <ListItem
        allow={['agency_licenses:manage']}
        to={LicensesMenuOptionsLinks.AGENCY_LICENSES}
        onClick={onClick}
      >
        <ListItemIcon>
          <FontAwesomeIcon size="lg" icon={faBuildingColumns} />
        </ListItemIcon>
        <ListItemText primary="Agency" />
      </ListItem>
      <ListItem
        allow={['agency_licenses:manage']}
        to={LicensesMenuOptionsLinks.AGENT_LICENSES}
        onClick={onClick}
      >
        <ListItemIcon>
          <FontAwesomeIcon
            size="lg"
            icon={faIdCard}
            style={{ position: 'relative', right: '0.1rem' }}
          />
        </ListItemIcon>
        <ListItemText primary="Agent " />
      </ListItem>
      <ListItem
        allow={['surplus_licenses:manage']}
        to={LicensesMenuOptionsLinks.SURUPLUS_LINES_LICENSES}
        onClick={onClick}
      >
        <ListItemIcon>
          <FontAwesomeIcon size="lg" icon={faSquareS} />
        </ListItemIcon>
        <ListItemText primary="Surplus Lines" />
      </ListItem>
    </List>
  );
};

const LicensesMenuOptionsLinks = {
  AGENCY_LICENSES: '/agency/manage/licenses',
  AGENT_LICENSES: '/agency/manage/agentlicenses',
  SURUPLUS_LINES_LICENSES: '/agency/manage/agentsurpluslicenses',
};

export const TechnicalMenuOptions = ({ onClick }: MenuOptionsProps) => {
  return (
    <List
      dense
      subheader={
        <ListSubheader style={{ fontWeight: 'bold', fontSize: '1.33rem' }}>
          Technical
        </ListSubheader>
      }
    >
      <ListItem
        allow={['audit_log:view']}
        to={ResourcesMenuOptionsLinks.ACTIVITY_LOG}
        onClick={onClick}
      >
        <ListItemIcon>
          <FontAwesomeIcon size="lg" icon={faClipboardList as IconProp} />
        </ListItemIcon>
        <ListItemText primary="Activity Log" />
      </ListItem>
      <ListItem
        allow={['api_keys:view']}
        to={ResourcesMenuOptionsLinks.DEVELOPERS}
        onClick={onClick}
      >
        <ListItemIcon>
          <FontAwesomeIcon
            size="lg"
            icon={faDisplayCode}
            style={{ position: 'relative', right: '0.2rem' }}
          />
        </ListItemIcon>
        <ListItemText primary="Developers" />
      </ListItem>
      <ListItem
        component="a"
        href={ResourcesMenuOptionsLinks.API_DOCS}
        onClick={onClick}
        target="_blank"
      >
        <ListItemIcon>
          <FontAwesomeIcon
            size="lg"
            icon={faBracketsCurly}
            style={{ position: 'relative', right: '0.2rem' }}
          />
        </ListItemIcon>
        <ListItemText primary="API Docs" />
      </ListItem>
      <ListItem to={ResourcesMenuOptionsLinks.SUPPORT} onClick={onClick}>
        <ListItemIcon>
          <FontAwesomeIcon size="lg" icon={faHeadset} />
        </ListItemIcon>
        <ListItemText primary="Support" />
      </ListItem>
    </List>
  );
};

const ResourcesMenuOptionsLinks = {
  ACTIVITY_LOG: '/agency/activity',
  DEVELOPERS: '/agency/developers/api-keys',
  API_DOCS: 'https://developers.cowbellcyber.ai',
  SUPPORT: '/agency/support',
};
